/**
 * What Reppr read from a contract's signed documents, and a person's decision
 * on terms it held. contract_term_extractions is agency-readable and
 * server-written, so decisions go through /api/contract-terms.
 */

import { pb, escapeFilterValue } from '../lib/pocketbase'

export type ContractReadStatus = 'read' | 'applied' | 'needs_review' | 'rejected' | 'unreadable' | 'failed'

export interface ContractRead {
  id: string
  contract_id: string
  document_id: string | null
  source_url: string | null
  status: ContractReadStatus
  form: string | null
  terms: {
    years: Array<{ year: number; base_salary: number; split_down_salary: number | null; quote: { page: number; text: string } }>
    payment: { cadence: string; period_weeks_options: number[]; quote: { page: number; text: string } } | null
    unread_amounts: Array<{ amount: number; page: number; context: string }>
    warnings: string[]
  } | null
  plan: { verdict: string; reasons: string[]; changes: Array<{ field: string; from: unknown; to: unknown; quote: { page: number; text: string } }> } | null
  error: string | null
  decided_by: string | null
  decided_at: string | null
  created: string
}

export async function listContractReads(contractId: string): Promise<ContractRead[]> {
  const rows = await pb.collection('contract_term_extractions').getFullList({
    filter: `contract_id = "${escapeFilterValue(contractId)}"`,
    sort: '-created',
  })
  return rows.map(r => ({
    id: r.id,
    contract_id: r.contract_id,
    document_id: r.document_id || null,
    source_url: r.source_url || null,
    status: r.status,
    form: r.form || null,
    terms: r.terms && typeof r.terms === 'object' && Array.isArray(r.terms.years) ? r.terms : null,
    plan: r.plan && typeof r.plan === 'object' ? r.plan : null,
    error: r.error || null,
    decided_by: r.decided_by || null,
    decided_at: r.decided_at || null,
    created: r.created,
  }))
}

export async function decideContractRead(extractionId: string, action: 'apply' | 'reject'): Promise<Record<string, any>> {
  const res = await fetch('/api/contract-terms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pb.authStore.token}` },
    body: JSON.stringify({ extraction_id: extractionId, action }),
  })
  const text = await res.text()
  let data: Record<string, any>
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(`Contract terms returned ${res.status} but not JSON. If you are running the dev server on its own, this endpoint only exists under \`vercel dev\`.`)
  }
  if (!res.ok) throw new Error(data.error || `Could not save the decision (${res.status}).`)
  return data
}
